import React from 'react';

export function PhoneMockup({ children, className = '', angle = false }) {
  return (
    <div
      className={`relative inline-block ${className}`}
      style={angle ? { transform: 'perspective(1200px) rotateY(-8deg) rotateX(2deg)' } : {}}
    >
      {/* iPhone 15 Pro frame */}
      <div className="relative rounded-[2.5rem] bg-[#1C1C1E] p-[3px] shadow-[0_20px_60px_rgba(0,0,0,0.4)]">
        {/* Dynamic Island */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 z-20 w-[90px] h-[26px] bg-[#1C1C1E] rounded-b-2xl" />
        {/* Screen */}
        <div className="relative rounded-[2.3rem] overflow-hidden" style={{ width: '270px', aspectRatio: '390/844', background: '#FDFBF7' }}>
          <div className="w-full h-full overflow-hidden">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

/* Mini dashboard mockup content — WARM WHITE */
export function DashboardMockupContent() {
  return (
    <div className="w-full h-full p-3 pt-8 text-[8px] overflow-hidden" style={{ background: '#FDFBF7', color: '#1A1A18' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-[7px]" style={{ color: '#9A9590' }}>Good evening</p>
          <p className="text-[13px]" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400, color: '#1A1A18' }}>Sarah</p>
        </div>
        <div className="w-6 h-6 rounded-full flex items-center justify-center text-[6px]" style={{ border: '1px solid #E8E3DB', color: '#9A9590' }}>S</div>
      </div>
      {/* Calorie ring + macros */}
      <div className="flex items-center gap-3 mb-3">
        <svg width="50" height="50" viewBox="0 0 50 50">
          <circle cx="25" cy="25" r="20" fill="none" stroke="#E8E3DB" strokeWidth="3" />
          <circle cx="25" cy="25" r="20" fill="none" stroke="#C67A5C" strokeWidth="3" strokeDasharray="82 126" strokeLinecap="round" transform="rotate(-90 25 25)" />
          <text x="25" y="24" textAnchor="middle" fill="#1A1A18" fontSize="8" fontWeight="500">1,340</text>
          <text x="25" y="31" textAnchor="middle" fill="#9A9590" fontSize="5">of 2,000</text>
        </svg>
        <div className="flex-1">
          <div className="flex justify-between mb-1">
            {[
              { label: 'Protein', val: '85g', color: '#5A8C6A' },
              { label: 'Carbs', val: '142g', color: '#C6A05C' },
              { label: 'Fat', val: '38g', color: '#C67A5C' },
            ].map(m => (
              <div key={m.label} className="text-center">
                <p className="text-[5px]" style={{ color: '#9A9590' }}>{m.label}</p>
                <p className="text-[9px] font-medium" style={{ color: m.color }}>{m.val}</p>
              </div>
            ))}
          </div>
          <div className="h-[2px] rounded-full" style={{ background: '#E8E3DB' }}>
            <div className="h-full rounded-full" style={{ width: '57%', background: '#5A8C6A' }} />
          </div>
          <p className="text-[5px] mt-0.5" style={{ color: '#9A9590' }}>65g protein to go</p>
        </div>
      </div>
      {/* Coaching card */}
      <div className="rounded-xl p-2 mb-2" style={{ background: '#F5F0E8' }}>
        <p className="text-[5px] uppercase font-medium mb-1" style={{ color: '#9A9590', letterSpacing: '0.5px' }}>Your next move</p>
        <p className="text-[7px] leading-relaxed" style={{ color: '#1A1A18' }}>You need <strong>65g more protein</strong> with 660 cal left.</p>
      </div>
      {/* Meals */}
      {[
        { name: 'Grilled chicken bowl', cals: '485', protein: '42', grade: 'B+', color: '#5A8C6A', bg: '#EBF3EE' },
        { name: 'Greek yogurt parfait', cals: '290', protein: '18', grade: 'A-', color: '#D4880F', bg: '#FFF8ED' },
        { name: 'Protein smoothie', cals: '320', protein: '30', grade: 'A', color: '#5A8C6A', bg: '#EBF3EE' },
      ].map((m, i) => (
        <div key={i} className="flex items-center gap-1.5 py-1.5" style={{ borderBottom: '1px solid #F0EBE3' }}>
          <div className="w-6 h-6 rounded-md flex items-center justify-center text-[6px] font-semibold" style={{ background: m.bg, color: m.color }}>{m.grade}</div>
          <div className="flex-1 min-w-0">
            <p className="text-[7px] font-medium truncate" style={{ color: '#1A1A18' }}>{m.name}</p>
            <p className="text-[5px]" style={{ color: '#9A9590' }}>{m.cals} cal · {m.protein}g protein</p>
          </div>
        </div>
      ))}
      {/* Water + Weight */}
      <div className="flex gap-1.5 mt-2">
        <div className="flex-1 rounded-lg p-1.5 text-center" style={{ background: '#FFFFFF', border: '1px solid #E8E3DB' }}>
          <p className="text-[5px]" style={{ color: '#9A9590' }}>Water</p>
          <p className="text-[9px] font-medium" style={{ color: '#1A1A18' }}>3<span className="text-[6px]" style={{ color: '#9A9590' }}>/10</span></p>
        </div>
        <div className="flex-1 rounded-lg p-1.5 text-center" style={{ background: '#FFFFFF', border: '1px solid #E8E3DB' }}>
          <p className="text-[5px]" style={{ color: '#9A9590' }}>Weight</p>
          <p className="text-[9px] font-medium" style={{ color: '#1A1A18' }}>166.4<span className="text-[6px]" style={{ color: '#5A8C6A' }}> ↓15.6</span></p>
        </div>
      </div>
    </div>
  );
}

/* Log meal camera mockup — with food image */
export function LogMealMockupContent() {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center relative overflow-hidden" style={{ background: '#FDFBF7' }}>
      <div className="absolute top-8 text-center z-10">
        <p className="text-[9px] font-medium" style={{ fontFamily: "'Playfair Display', serif", color: '#1A1A18' }}>Log a meal</p>
      </div>
      {/* Viewfinder with food */}
      <div className="w-[85%] aspect-square rounded-2xl relative overflow-hidden" style={{ border: '2px dashed #E8E3DB', background: '#F5F0E8' }}>
        <img src="/food-images/snap_meal_camera.png" alt="AI analyzing meal" className="w-full h-full object-cover" />
        {/* Corner brackets */}
        <div className="absolute top-1 left-1 w-4 h-4 border-t-2 border-l-2 rounded-tl" style={{ borderColor: '#C67A5C' }} />
        <div className="absolute top-1 right-1 w-4 h-4 border-t-2 border-r-2 rounded-tr" style={{ borderColor: '#C67A5C' }} />
        <div className="absolute bottom-1 left-1 w-4 h-4 border-b-2 border-l-2 rounded-bl" style={{ borderColor: '#C67A5C' }} />
        <div className="absolute bottom-1 right-1 w-4 h-4 border-b-2 border-r-2 rounded-br" style={{ borderColor: '#C67A5C' }} />
        {/* AI overlay labels */}
        <div className="absolute top-3 left-3 text-white text-[5px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: 'rgba(26,26,24,0.7)' }}>
          Salmon — 280 cal
        </div>
        <div className="absolute top-8 right-3 text-white text-[5px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: 'rgba(26,26,24,0.7)' }}>
          Asparagus — 40 cal
        </div>
        <div className="absolute bottom-6 left-6 text-white text-[5px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: 'rgba(26,26,24,0.7)' }}>
          Quinoa — 180 cal
        </div>
        {/* Scanning line */}
        <div className="absolute inset-x-0 top-1/2 h-[1px]" style={{ background: '#5A8C6A80' }} />
      </div>
      {/* Shutter */}
      <div className="mt-4 w-12 h-12 rounded-full flex items-center justify-center" style={{ border: '3px solid #E8E3DB' }}>
        <div className="w-9 h-9 rounded-full" style={{ background: '#C67A5C' }} />
      </div>
      <p className="text-[6px] mt-1.5 font-medium" style={{ color: '#5A8C6A' }}>AI identifying items...</p>
    </div>
  );
}

/* Follow-up question mockup — WARM WHITE */
export function FollowUpMockupContent() {
  return (
    <div className="w-full h-full p-3 pt-8 text-[8px] overflow-hidden" style={{ background: '#FDFBF7', color: '#1A1A18' }}>
      <p className="text-[7px] mb-1" style={{ color: '#9A9590' }}>Follow-up 1 of 3</p>
      <div className="rounded-xl p-3 mb-2" style={{ background: '#FFFFFF', border: '1px solid #E8E3DB' }}>
        <p className="text-[8px] font-semibold mb-2" style={{ color: '#1A1A18' }}>Was there dressing on the salad?</p>
        <div className="space-y-1.5">
          {['Ranch dressing', 'Caesar dressing', 'Balsamic vinaigrette', 'No dressing'].map((opt, i) => (
            <button key={i} className="w-full text-left px-2 py-1.5 rounded-lg text-[7px]" style={{
              border: i === 0 ? '1px solid #5A8C6A' : '1px solid #E8E3DB',
              background: i === 0 ? '#EBF3EE' : '#FFFFFF',
              color: i === 0 ? '#5A8C6A' : '#9A9590',
              fontWeight: i === 0 ? 500 : 400,
            }}>
              {opt} {i === 0 && <span className="text-[6px]" style={{ color: '#C67A5C' }}>+180 cal</span>}
            </button>
          ))}
        </div>
      </div>
      <div className="rounded-xl p-3 mb-2" style={{ background: '#FFFFFF', border: '1px solid #E8E3DB' }}>
        <p className="text-[8px] font-semibold mb-2" style={{ color: '#1A1A18' }}>How was this cooked?</p>
        <div className="flex flex-wrap gap-1">
          {['Grilled', 'Fried', 'Baked', 'Raw'].map((opt, i) => (
            <span key={i} className="px-2 py-1 rounded-full text-[7px]" style={{
              background: i === 0 ? '#1A1A18' : '#F5F0E8',
              color: i === 0 ? '#FDFBF7' : '#9A9590',
            }}>{opt}</span>
          ))}
        </div>
      </div>
      <div className="rounded-xl p-3 mb-2" style={{ background: '#FFFFFF', border: '1px solid #E8E3DB' }}>
        <p className="text-[8px] font-semibold mb-2" style={{ color: '#1A1A18' }}>Any extra toppings or sides?</p>
        <div className="space-y-1.5">
          {['Croutons (handful)', 'Parmesan cheese', 'Bacon bits', 'None'].map((opt, i) => (
            <button key={i} className="w-full text-left px-2 py-1.5 rounded-lg text-[7px]" style={{
              border: i < 2 ? '1px solid #5A8C6A' : '1px solid #E8E3DB',
              background: i < 2 ? '#EBF3EE' : '#FFFFFF',
              color: i < 2 ? '#5A8C6A' : '#9A9590',
              fontWeight: i < 2 ? 500 : 400,
            }}>
              {opt} {i === 0 && <span className="text-[6px]" style={{ color: '#C67A5C' }}>+55 cal</span>}
              {i === 1 && <span className="text-[6px]" style={{ color: '#C67A5C' }}>+22 cal</span>}
            </button>
          ))}
        </div>
      </div>
      {/* Progress indicator */}
      <div className="flex items-center gap-2 px-1 mt-1">
        <div className="flex-1 h-1 rounded-full" style={{ background: '#E8E3DB' }}>
          <div className="h-full rounded-full" style={{ width: '33%', background: '#5A8C6A' }} />
        </div>
        <span className="text-[6px]" style={{ color: '#9A9590' }}>1 of 3</span>
      </div>
    </div>
  );
}

/* Food detail / confirmed result — WARM WHITE */
export function FoodDetailMockupContent() {
  return (
    <div className="w-full h-full text-[8px] overflow-hidden" style={{ background: '#FDFBF7', color: '#1A1A18' }}>
      {/* Warm gradient hero */}
      <div className="flex items-center justify-center" style={{ height: 70, background: 'linear-gradient(135deg, #F9E8D9, #F5DFC9)' }}>
        <span style={{ fontSize: 32 }}>&#129367;</span>
      </div>
      <div className="p-3">
        {/* Grade + time */}
        <div className="flex items-center gap-1 mb-1">
          <span className="text-[7px] font-semibold px-1.5 py-0.5 rounded-md" style={{ background: '#EBF3EE', color: '#5A8C6A' }}>B+</span>
          <span className="text-[6px]" style={{ color: '#9A9590' }}>Lunch · 12:30pm</span>
        </div>
        <p className="text-[10px] mb-0.5" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400, color: '#1A1A18' }}>Chicken caesar salad</p>
        <p className="text-[6px] font-medium mb-2" style={{ color: '#C67A5C' }}>+205 cal caught by follow-ups</p>
        {/* Macros */}
        <div className="grid grid-cols-4 gap-1 text-center mb-2">
          {[
            { label: 'Calories', val: '485', color: '#C67A5C' },
            { label: 'Protein', val: '38g', color: '#5A8C6A' },
            { label: 'Carbs', val: '24g', color: '#C6A05C' },
            { label: 'Fat', val: '22g', color: '#C67A5C' },
          ].map(m => (
            <div key={m.label}>
              <p className="text-[9px] font-medium" style={{ color: m.color }}>{m.val}</p>
              <p className="text-[5px]" style={{ color: '#9A9590' }}>{m.label}</p>
            </div>
          ))}
        </div>
        {/* Health Score breakdown */}
        <div className="rounded-lg p-2 mb-2" style={{ background: '#FFFFFF', border: '1px solid #E8E3DB' }}>
          <p className="text-[5px] uppercase font-medium mb-1" style={{ color: '#9A9590', letterSpacing: '0.5px' }}>Health Score</p>
          <div className="h-[3px] rounded-full mb-1" style={{ background: '#E8E3DB' }}>
            <div className="h-full rounded-full" style={{ width: '72%', background: '#5A8C6A' }} />
          </div>
          <div className="flex justify-between text-[5px]" style={{ color: '#9A9590' }}>
            <span>Protein: <strong style={{ color: '#5A8C6A' }}>Great</strong></span>
            <span>Fiber: <strong style={{ color: '#C6A05C' }}>OK</strong></span>
            <span>Sodium: <strong style={{ color: '#C67A5C' }}>High</strong></span>
          </div>
        </div>
        {/* Ingredients */}
        <p className="text-[5px] uppercase font-medium mb-1" style={{ color: '#9A9590', letterSpacing: '0.5px' }}>Ingredients</p>
        {[
          { name: 'Grilled chicken breast (6oz)', cal: 190, caught: false },
          { name: 'Romaine lettuce', cal: 15, caught: false },
          { name: 'Croutons (handful)', cal: 55, caught: true },
          { name: 'Ranch dressing (2 tbsp)', cal: 145, caught: true },
          { name: 'Parmesan (1 tbsp)', cal: 22, caught: true },
          { name: 'Olive oil drizzle', cal: 58, caught: true },
        ].map((item, i) => (
          <div key={i} className="flex justify-between py-0.5" style={{ borderBottom: '1px solid #F0EBE3' }}>
            <span className="text-[6px]" style={{ color: item.caught ? '#C67A5C' : '#1A1A18', fontWeight: item.caught ? 500 : 400 }}>
              {item.caught && '● '}{item.name}
            </span>
            <span className="text-[6px]" style={{ color: item.caught ? '#C67A5C' : '#9A9590' }}>{item.cal}</span>
          </div>
        ))}
        {/* Caught card */}
        <div className="mt-1.5 p-1.5 rounded-lg" style={{ border: '1.5px dashed #C67A5C' }}>
          <p className="text-[6px] font-medium" style={{ color: '#C67A5C' }}>Caught by follow-up questions</p>
          <p className="text-[5px]" style={{ color: '#6A6560' }}>+205 cal a photo alone would miss.</p>
        </div>
        {/* AI Coaching suggestion */}
        <div className="mt-1.5 p-1.5 rounded-lg" style={{ background: '#EBF3EE', border: '1px solid #5A8C6A30' }}>
          <p className="text-[5px] uppercase font-medium mb-0.5" style={{ color: '#5A8C6A', letterSpacing: '0.5px' }}>AI Coach tip</p>
          <p className="text-[6px] leading-relaxed" style={{ color: '#1A1A18' }}>Swap ranch for balsamic vinaigrette to save 110 cal and keep the flavor.</p>
        </div>
        {/* Micronutrients preview */}
        <div className="mt-1.5 rounded-lg p-1.5" style={{ background: '#FFFFFF', border: '1px solid #E8E3DB' }}>
          <p className="text-[5px] uppercase font-medium mb-1" style={{ color: '#9A9590', letterSpacing: '0.5px' }}>Key Nutrients</p>
          <div className="grid grid-cols-3 gap-1">
            {[
              { name: 'Fiber', val: '4g', pct: 16 },
              { name: 'Sodium', val: '920mg', pct: 40 },
              { name: 'Vitamin A', val: '85%', pct: 85 },
            ].map(n => (
              <div key={n.name} className="text-center">
                <p className="text-[7px] font-medium" style={{ color: '#1A1A18' }}>{n.val}</p>
                <div className="h-[2px] rounded-full mx-1" style={{ background: '#E8E3DB' }}>
                  <div className="h-full rounded-full" style={{ width: `${n.pct}%`, background: n.pct > 30 ? '#5A8C6A' : '#C6A05C' }} />
                </div>
                <p className="text-[4px]" style={{ color: '#9A9590' }}>{n.name}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* AI Coach mockup — WARM WHITE */
export function CoachMockupContent({ variant = 'build' }) {
  const conversations = {
    build: {
      user: "I need 52g more protein within 580 cal. What should I eat?",
      coach: (
        <>
          <strong style={{ color: '#5A8C6A' }}>Garlic Lemon Salmon</strong><br />
          <span className="text-[6px]" style={{ color: '#9A9590' }}>48g protein | 560 cal | 12g fat</span><br />
          <span className="text-[6px]" style={{ color: '#9A9590' }}>6oz salmon, lemon, garlic, asparagus, rice</span>
          <div className="mt-1 flex gap-1">
            <span className="text-[5px] px-1.5 py-0.5 rounded-full" style={{ background: '#F5F0E8', color: '#6A6560' }}>View recipe</span>
            <span className="text-[5px] px-1.5 py-0.5 rounded-full" style={{ background: '#C67A5C', color: '#FFFFFF' }}>Log this</span>
          </div>
        </>
      )
    },
    optimize: {
      user: "Optimize my day — where can I improve?",
      coach: (
        <>
          <strong style={{ color: '#5A8C6A' }}>2 easy swaps to hit your protein:</strong><br />
          <span className="text-[6px]" style={{ color: '#6A6560' }}>
            Breakfast: Swap milk &rarr; Greek yogurt<br />
            <span style={{ color: '#5A8C6A' }}>+12g protein, +20 cal</span>
          </span><br />
          <span className="text-[6px]" style={{ color: '#6A6560' }}>
            Lunch: Swap chips &rarr; side salad<br />
            <span style={{ color: '#5A8C6A' }}>-150 cal, +3g fiber</span>
          </span>
        </>
      )
    },
    restaurant: {
      user: "I'm eating at Chipotle tonight. What should I order?",
      coach: (
        <>
          <strong style={{ color: '#5A8C6A' }}>Chicken Burrito Bowl</strong><br />
          <span className="text-[6px]" style={{ color: '#6A6560' }}>
            Chicken, brown rice, black beans, fajita veggies, fresh tomato salsa, lettuce<br />
            <span className="font-semibold" style={{ color: '#1A1A18' }}>665 cal | 52g P | 72g C | 18g F</span>
          </span><br />
          <span className="text-[6px]" style={{ color: '#9A9590' }}>Skip the sour cream (-110 cal) and cheese (-100 cal) to stay within budget.</span>
        </>
      )
    }
  };
  const c = conversations[variant];
  return (
    <div className="w-full h-full p-3 pt-8 text-[7px] overflow-hidden" style={{ background: '#FDFBF7', color: '#1A1A18' }}>
      <div className="flex items-center gap-1.5 mb-3">
        <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: '#F5F0E8' }}>
          <span className="text-[8px]">&#127807;</span>
        </div>
        <span className="text-[8px] font-medium" style={{ color: '#1A1A18' }}>Nuri</span>
      </div>
      {/* User msg */}
      <div className="flex justify-end mb-2">
        <div className="px-2 py-1.5 rounded-xl rounded-br-sm text-[7px] max-w-[85%]" style={{ background: '#1A1A18', color: '#FDFBF7' }}>
          {c.user}
        </div>
      </div>
      {/* Coach msg */}
      <div className="flex justify-start">
        <div className="px-2 py-1.5 rounded-xl rounded-bl-sm text-[7px] max-w-[90%]" style={{ background: '#F5F0E8', color: '#1A1A18' }}>
          {c.coach}
        </div>
      </div>
    </div>
  );
}

/* Optimize Day mockup — WARM WHITE */
export function OptimizeMockupContent() {
  return (
    <div className="w-full h-full p-3 pt-8 text-[8px] overflow-hidden" style={{ background: '#FDFBF7', color: '#1A1A18' }}>
      <p className="text-[9px] font-medium mb-2" style={{ color: '#1A1A18' }}>Optimize My Day</p>
      {/* Summary */}
      <div className="grid grid-cols-2 gap-1 mb-2">
        <div className="rounded-lg p-1.5 text-center" style={{ background: '#FFFFFF', border: '1px solid #E8E3DB' }}>
          <p className="text-[6px]" style={{ color: '#9A9590' }}>Original</p>
          <p className="text-[9px] font-medium" style={{ color: '#1A1A18' }}>1,840 cal</p>
          <p className="text-[6px]" style={{ color: '#9A9590' }}>98g protein</p>
        </div>
        <div className="rounded-lg p-1.5 text-center" style={{ background: '#EBF3EE', border: '1px solid #5A8C6A30' }}>
          <p className="text-[6px]" style={{ color: '#5A8C6A' }}>Optimized</p>
          <p className="text-[9px] font-medium" style={{ color: '#5A8C6A' }}>1,780 cal</p>
          <p className="text-[6px]" style={{ color: '#5A8C6A' }}>132g protein</p>
        </div>
      </div>
      {/* Swaps */}
      {[
        { from: 'Whole milk in coffee', to: 'Greek yogurt parfait', cal: '+20', prot: '+12g' },
        { from: 'Potato chips (lunch)', to: 'Side salad w/ vinaigrette', cal: '-150', prot: '+3g' },
        { from: 'White rice (dinner)', to: 'Quinoa pilaf', cal: '-30', prot: '+6g' },
      ].map((s, i) => (
        <div key={i} className="rounded-lg p-1.5 mb-1" style={{ background: '#FFFFFF', border: '1px solid #E8E3DB' }}>
          <div className="flex items-center gap-1 mb-0.5">
            <span className="text-[6px] line-through" style={{ color: '#C45B5B' }}>{s.from}</span>
            <span className="text-[6px]" style={{ color: '#9A9590' }}>&rarr;</span>
            <span className="text-[6px] font-semibold" style={{ color: '#5A8C6A' }}>{s.to}</span>
          </div>
          <div className="flex gap-1">
            <span className="text-[5px] px-1 py-0.5 rounded-full" style={{ background: '#EBF3EE', color: '#5A8C6A' }}>{s.cal} cal</span>
            <span className="text-[5px] px-1 py-0.5 rounded-full" style={{ background: '#EBF3EE', color: '#5A8C6A' }}>{s.prot} protein</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/* Recipe builder mockup — WARM WHITE */
export function RecipeMockupContent() {
  return (
    <div className="w-full h-full p-3 pt-8 text-[8px] overflow-hidden" style={{ background: '#FDFBF7', color: '#1A1A18' }}>
      <p className="text-[9px] font-medium mb-1" style={{ color: '#1A1A18' }}>Mom's Chicken Enchiladas</p>
      <span className="text-[6px] px-1.5 py-0.5 rounded-full" style={{ background: '#F5F0E8', color: '#C67A5C' }}>Dinner</span>
      <div className="rounded-lg p-2 mt-2 mb-2" style={{ background: '#FFFFFF', border: '1px solid #E8E3DB' }}>
        <p className="text-[7px] font-medium mb-1" style={{ color: '#1A1A18' }}>Ingredients</p>
        {['Chicken breast (12oz)', 'Flour tortillas (4)', 'Enchilada sauce (1 cup)', 'Cheddar cheese (1/2 cup)', 'Sour cream (2 tbsp)'].map((item, i) => (
          <div key={i} className="flex justify-between py-0.5" style={{ borderBottom: '1px solid #F0EBE3' }}>
            <span className="text-[6px]" style={{ color: '#6A6560' }}>{item}</span>
            <span className="text-[6px]" style={{ color: '#9A9590' }}>{[280, 480, 60, 220, 60][i]} cal</span>
          </div>
        ))}
      </div>
      {/* Running total */}
      <div className="rounded-lg p-2" style={{ background: '#FFFFFF', border: '1px solid #E8E3DB' }}>
        <p className="text-[6px] mb-1" style={{ color: '#9A9590' }}>Total / Per Serving (4 servings)</p>
        <div className="grid grid-cols-4 gap-1 text-center">
          {[
            { label: 'Cal', per: '275', color: '#C67A5C' },
            { label: 'Prot', per: '22g', color: '#5A8C6A' },
            { label: 'Carbs', per: '24g', color: '#C6A05C' },
            { label: 'Fat', per: '10.5g', color: '#C67A5C' },
          ].map(m => (
            <div key={m.label}>
              <p className="text-[7px] font-medium" style={{ color: m.color }}>{m.per}</p>
              <p className="text-[5px]" style={{ color: '#9A9590' }}>{m.label}/srv</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* Fridge scanner mockup — WARM WHITE */
export function FridgeMockupContent() {
  return (
    <div className="w-full h-full p-3 pt-8 text-[8px] overflow-hidden" style={{ background: '#FDFBF7', color: '#1A1A18' }}>
      <p className="text-[9px] font-medium mb-1" style={{ color: '#1A1A18' }}>Fridge Scan Results</p>
      <p className="text-[6px] mb-2" style={{ color: '#9A9590' }}>Found 6 ingredients. Here's what you can make:</p>
      {[
        { name: 'Chicken Stir-Fry', cal: 420, prot: 38, time: '15 min', score: 'A' },
        { name: 'Greek Chicken Wrap', cal: 380, prot: 32, time: '10 min', score: 'A-' },
        { name: 'Egg & Veggie Scramble', cal: 290, prot: 24, time: '8 min', score: 'B+' },
      ].map((r, i) => (
        <div key={i} className="rounded-lg p-2 mb-1" style={{ background: '#FFFFFF', border: '1px solid #E8E3DB' }}>
          <div className="flex items-center justify-between mb-0.5">
            <p className="text-[7px] font-medium" style={{ color: '#1A1A18' }}>{r.name}</p>
            <span className="text-[6px] font-semibold px-1 py-0.5 rounded" style={{ background: '#EBF3EE', color: '#5A8C6A' }}>{r.score}</span>
          </div>
          <div className="flex gap-2 text-[6px]" style={{ color: '#9A9590' }}>
            <span>{r.cal} cal</span>
            <span>{r.prot}g protein</span>
            <span>{r.time}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/* GLP-1 tracker mockup — WARM WHITE */
export function GLP1MockupContent() {
  return (
    <div className="w-full h-full p-3 pt-8 text-[8px] overflow-hidden" style={{ background: '#FDFBF7', color: '#1A1A18' }}>
      <p className="text-[9px] font-medium mb-2" style={{ color: '#1A1A18' }}>GLP-1 Tracker</p>
      <div className="rounded-lg p-2 mb-2" style={{ background: '#FFFFFF', border: '1px solid #E8E3DB' }}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[7px] font-medium" style={{ color: '#1A1A18' }}>Next Injection</span>
          <span className="text-[7px] font-semibold" style={{ color: '#C67A5C' }}>In 2 days</span>
        </div>
        <p className="text-[6px]" style={{ color: '#9A9590' }}>Last: Left Abdomen | 0.5mg | Mar 20</p>
      </div>
      <div className="rounded-lg p-2 mb-2" style={{ background: '#FFFFFF', border: '1px solid #E8E3DB' }}>
        <p className="text-[7px] font-medium mb-1" style={{ color: '#1A1A18' }}>Injection Sites</p>
        <div className="grid grid-cols-3 gap-1">
          {['L. Abdomen', 'R. Abdomen', 'L. Thigh', 'R. Thigh', 'L. Arm', 'R. Arm'].map((s, i) => (
            <span key={i} className="text-[5px] px-1 py-0.5 rounded text-center" style={{
              background: i === 0 ? '#EBF3EE' : '#F5F0E8',
              color: i === 0 ? '#5A8C6A' : '#9A9590',
              fontWeight: i === 0 ? 500 : 400,
            }}>{s}</span>
          ))}
        </div>
      </div>
      <div className="rounded-lg p-2" style={{ background: '#FFF8ED', border: '1px solid #C6A05C30' }}>
        <p className="text-[6px] font-medium" style={{ color: '#C67A5C' }}>Protein Priority: 140g/day</p>
        <p className="text-[5px]" style={{ color: '#9A9590' }}>GLP-1 users need 1.0-1.2g/kg to prevent muscle loss</p>
      </div>
    </div>
  );
}
